import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  DollarSign, 
  Star, 
  Users, 
  Activity, 
  Clock, 
  Zap,
  AlertTriangle,
  CheckCircle,
  Eye,
  MousePointer,
  RefreshCw,
  Filter,
  Download,
  Calendar,
  Globe,
  Cpu,
  Database
} from 'lucide-react';

interface MonitoringDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: number;
  icon: React.ComponentType<any>;
  color: string;
  trend: 'up' | 'down' | 'stable';
}

interface UsageMetric {
  timestamp: string;
  requests: number;
  responseTime: number;
  errorRate: number;
  activeUsers: number;
}

interface CostMetric {
  service: string;
  cost: number;
  requests: number;
  costPerRequest: number;
  trend: number;
}

interface SatisfactionRating {
  agentId: string;
  agentName: string;
  rating: number;
  totalRatings: number;
  satisfaction: number;
  usabilityScore: number;
}

interface ABTestResult {
  testId: string;
  testName: string;
  variant: string;
  conversionRate: number;
  confidence: number;
  status: 'running' | 'completed' | 'paused';
}

export function MonitoringDashboard({ isOpen, onClose }: MonitoringDashboardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'usage' | 'costs' | 'satisfaction' | 'abtests'>('overview');
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [metrics, setMetrics] = useState<MetricCard[]>([]);
  const [usageData, setUsageData] = useState<UsageMetric[]>([]);
  const [costData, setCostData] = useState<CostMetric[]>([]);
  const [satisfactionData, setSatisfactionData] = useState<SatisfactionRating[]>([]);
  const [abTestData, setABTestData] = useState<ABTestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadMonitoringData();
      
      if (autoRefresh) {
        const interval = setInterval(loadMonitoringData, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
      }
    }
  }, [isOpen, timeRange, autoRefresh]);

  const loadMonitoringData = async () => {
    setLoading(true);
    
    try {
      // Simulate API calls to monitoring services
      await Promise.all([
        loadOverviewMetrics(),
        loadUsageMetrics(),
        loadCostMetrics(),
        loadSatisfactionMetrics(),
        loadABTestMetrics()
      ]);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewMetrics = async () => {
    // Simulate real-time metrics
    const mockMetrics: MetricCard[] = [
      {
        title: 'Total Requests',
        value: '2.4M',
        change: 12.5,
        icon: Activity,
        color: 'text-blue-600',
        trend: 'up'
      },
      {
        title: 'Active Users',
        value: '8,432',
        change: 8.2,
        icon: Users,
        color: 'text-green-600',
        trend: 'up'
      },
      {
        title: 'Avg Response Time',
        value: '245ms',
        change: -5.3,
        icon: Clock,
        color: 'text-purple-600',
        trend: 'up'
      },
      {
        title: 'Error Rate',
        value: '0.12%',
        change: -15.7,
        icon: AlertTriangle,
        color: 'text-red-600',
        trend: 'up'
      },
      {
        title: 'Total Cost',
        value: '$1,247',
        change: 3.8,
        icon: DollarSign,
        color: 'text-yellow-600',
        trend: 'down'
      },
      {
        title: 'Satisfaction Score',
        value: '4.7/5',
        change: 2.1,
        icon: Star,
        color: 'text-orange-600',
        trend: 'up'
      }
    ];
    
    setMetrics(mockMetrics);
  };

  const loadUsageMetrics = async () => {
    // Generate mock usage data
    const now = new Date();
    const data: UsageMetric[] = [];
    
    for (let i = 23; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - i * 60 * 60 * 1000);
      data.push({
        timestamp: timestamp.toISOString(),
        requests: Math.floor(Math.random() * 1000) + 500,
        responseTime: Math.floor(Math.random() * 200) + 150,
        errorRate: Math.random() * 0.5,
        activeUsers: Math.floor(Math.random() * 200) + 300
      });
    }
    
    setUsageData(data);
  };

  const loadCostMetrics = async () => {
    const mockCosts: CostMetric[] = [
      {
        service: 'OpenAI GPT-4',
        cost: 847.32,
        requests: 45230,
        costPerRequest: 0.0187,
        trend: 5.2
      },
      {
        service: 'Google Gemini',
        cost: 234.56,
        requests: 23450,
        costPerRequest: 0.0100,
        trend: -2.1
      },
      {
        service: 'Anthropic Claude',
        cost: 165.78,
        requests: 12340,
        costPerRequest: 0.0134,
        trend: 8.7
      },
      {
        service: 'Hosting (Netlify)',
        cost: 89.00,
        requests: 0,
        costPerRequest: 0,
        trend: 0
      },
      {
        service: 'Database (Supabase)',
        cost: 45.00,
        requests: 0,
        costPerRequest: 0,
        trend: 0
      }
    ];
    
    setCostData(mockCosts);
  };

  const loadSatisfactionMetrics = async () => {
    const mockSatisfaction: SatisfactionRating[] = [
      {
        agentId: '1',
        agentName: 'CustomerBot Pro',
        rating: 4.8,
        totalRatings: 234,
        satisfaction: 92,
        usabilityScore: 89
      },
      {
        agentId: '2',
        agentName: 'ContentGenie',
        rating: 4.6,
        totalRatings: 189,
        satisfaction: 88,
        usabilityScore: 91
      },
      {
        agentId: '3',
        agentName: 'DataInsight AI',
        rating: 4.7,
        totalRatings: 156,
        satisfaction: 90,
        usabilityScore: 85
      },
      {
        agentId: '4',
        agentName: 'CodeAssistant',
        rating: 4.5,
        totalRatings: 298,
        satisfaction: 85,
        usabilityScore: 88
      }
    ];
    
    setSatisfactionData(mockSatisfaction);
  };

  const loadABTestMetrics = async () => {
    const mockABTests: ABTestResult[] = [
      {
        testId: 'test_1',
        testName: 'Agent Card Layout',
        variant: 'Grid vs List',
        conversionRate: 23.4,
        confidence: 95,
        status: 'running'
      },
      {
        testId: 'test_2',
        testName: 'Deployment Button Color',
        variant: 'Blue vs Green',
        conversionRate: 18.7,
        confidence: 87,
        status: 'running'
      },
      {
        testId: 'test_3',
        testName: 'Onboarding Flow',
        variant: 'Short vs Detailed',
        conversionRate: 31.2,
        confidence: 99,
        status: 'completed'
      },
      {
        testId: 'test_4',
        testName: 'Search Suggestions',
        variant: 'AI vs Manual',
        conversionRate: 15.8,
        confidence: 72,
        status: 'paused'
      }
    ];
    
    setABTestData(mockABTests);
  };

  const exportData = () => {
    const data = {
      metrics,
      usage: usageData,
      costs: costData,
      satisfaction: satisfactionData,
      abTests: abTestData,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-store-metrics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-green-600 bg-green-50';
      case 'completed': return 'text-blue-600 bg-blue-50';
      case 'paused': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Monitoring & Insights</h2>
              <p className="text-sm opacity-90">Real-time analytics and performance metrics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                Auto-refresh
              </label>
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            </div>
            
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-1 bg-white/20 border border-white/30 rounded-lg text-sm"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-3 py-1 bg-white/20 rounded-lg text-sm hover:bg-white/30 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'usage', label: 'Usage Analytics', icon: Activity },
              { id: 'costs', label: 'Cost Tracking', icon: DollarSign },
              { id: 'satisfaction', label: 'User Satisfaction', icon: Star },
              { id: 'abtests', label: 'A/B Tests', icon: Zap }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading monitoring data...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {metrics.map((metric, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                          <div className={`p-3 rounded-lg ${metric.color.replace('text-', 'bg-').replace('-600', '-100')}`}>
                            <metric.icon className={`w-6 h-6 ${metric.color}`} />
                          </div>
                          {getTrendIcon(metric.trend)}
                        </div>
                        
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{metric.value}</h3>
                        <p className="text-sm text-gray-600 mb-2">{metric.title}</p>
                        
                        <div className={`flex items-center gap-1 text-sm ${
                          metric.change > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          <span>{metric.change > 0 ? '+' : ''}{metric.change}%</span>
                          <span className="text-gray-500">vs last period</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Real-time Activity */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Real-time Activity</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">127</div>
                        <div className="text-sm text-gray-600">Requests/min</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">43</div>
                        <div className="text-sm text-gray-600">Active Users</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">98.7%</div>
                        <div className="text-sm text-gray-600">Uptime</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">234ms</div>
                        <div className="text-sm text-gray-600">Avg Response</div>
                      </div>
                    </div>
                  </div>

                  {/* System Health */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm">API Gateway</span>
                          </div>
                          <span className="text-sm text-green-600">Healthy</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm">Database</span>
                          </div>
                          <span className="text-sm text-green-600">Healthy</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-500" />
                            <span className="text-sm">AI Services</span>
                          </div>
                          <span className="text-sm text-yellow-600">Degraded</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="text-sm">CDN</span>
                          </div>
                          <span className="text-sm text-green-600">Healthy</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Agents</h3>
                      <div className="space-y-3">
                        {satisfactionData.slice(0, 4).map((agent, index) => (
                          <div key={agent.agentId} className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-sm">{agent.agentName}</div>
                              <div className="text-xs text-gray-500">{agent.totalRatings} ratings</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Star className="w-4 h-4 text-yellow-500" />
                              <span className="text-sm font-medium">{agent.rating}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'usage' && (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Trends</h3>
                    <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">Interactive usage chart would be here</p>
                        <p className="text-xs text-gray-400">Showing {usageData.length} data points</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Average Response Time</span>
                            <span>245ms</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '75%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Success Rate</span>
                            <span>99.88%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-green-600 h-2 rounded-full" style={{ width: '99%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>CPU Usage</span>
                            <span>34%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '34%' }}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Memory Usage</span>
                            <span>67%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-orange-600 h-2 rounded-full" style={{ width: '67%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">User Engagement</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Eye className="w-5 h-5 text-blue-500" />
                            <span className="text-sm">Page Views</span>
                          </div>
                          <span className="font-medium">24,567</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MousePointer className="w-5 h-5 text-green-500" />
                            <span className="text-sm">Agent Interactions</span>
                          </div>
                          <span className="font-medium">8,432</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-purple-500" />
                            <span className="text-sm">Unique Users</span>
                          </div>
                          <span className="font-medium">3,245</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-orange-500" />
                            <span className="text-sm">Avg Session Duration</span>
                          </div>
                          <span className="font-medium">4m 32s</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'costs' && (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Cost Breakdown</h3>
                      <div className="text-2xl font-bold text-gray-900">
                        ${costData.reduce((sum, item) => sum + item.cost, 0).toFixed(2)}
                        <span className="text-sm font-normal text-gray-500 ml-2">this month</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {costData.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              {item.service.includes('OpenAI') && <Cpu className="w-5 h-5 text-blue-600" />}
                              {item.service.includes('Google') && <Globe className="w-5 h-5 text-green-600" />}
                              {item.service.includes('Anthropic') && <Zap className="w-5 h-5 text-purple-600" />}
                              {item.service.includes('Hosting') && <Globe className="w-5 h-5 text-orange-600" />}
                              {item.service.includes('Database') && <Database className="w-5 h-5 text-gray-600" />}
                            </div>
                            <div>
                              <div className="font-medium">{item.service}</div>
                              <div className="text-sm text-gray-500">
                                {item.requests > 0 ? `${item.requests.toLocaleString()} requests` : 'Fixed cost'}
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="font-bold text-lg">${item.cost.toFixed(2)}</div>
                            {item.costPerRequest > 0 && (
                              <div className="text-sm text-gray-500">
                                ${item.costPerRequest.toFixed(4)}/req
                              </div>
                            )}
                            <div className={`text-xs flex items-center gap-1 ${
                              item.trend > 0 ? 'text-red-600' : item.trend < 0 ? 'text-green-600' : 'text-gray-500'
                            }`}>
                              {item.trend !== 0 && (
                                <>
                                  {item.trend > 0 ? '↗' : '↘'}
                                  {Math.abs(item.trend)}%
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Optimization</h3>
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Potential Savings</span>
                          </div>
                          <p className="text-xs text-green-600 mt-1">
                            Switch to Google Gemini for 40% cost reduction
                          </p>
                        </div>
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-2 text-yellow-700">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm font-medium">Usage Alert</span>
                          </div>
                          <p className="text-xs text-yellow-600 mt-1">
                            OpenAI usage increased 25% this week
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Tracking</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Monthly Budget</span>
                            <span>$1,500</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '83%' }}></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">83% used ($1,247 / $1,500)</div>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <div className="flex justify-between">
                            <span>Projected month-end:</span>
                            <span className="font-medium">$1,456</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Remaining budget:</span>
                            <span className="font-medium text-green-600">$253</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'satisfaction' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">4.7</div>
                      <div className="text-sm text-gray-600 mb-2">Average Rating</div>
                      <div className="flex justify-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-4 h-4 ${star <= 4.7 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">89%</div>
                      <div className="text-sm text-gray-600">Satisfaction Rate</div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl p-6 text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-2">877</div>
                      <div className="text-sm text-gray-600">Total Reviews</div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Satisfaction Scores</h3>
                    <div className="space-y-4">
                      {satisfactionData.map((agent) => (
                        <div key={agent.agentId} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-medium">{agent.agentName}</h4>
                              <p className="text-sm text-gray-500">{agent.totalRatings} ratings</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Star className="w-5 h-5 text-yellow-400 fill-current" />
                              <span className="font-bold text-lg">{agent.rating}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span>Satisfaction</span>
                                <span>{agent.satisfaction}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full" 
                                  style={{ width: `${agent.satisfaction}%` }}
                                ></div>
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span>Usability</span>
                                <span>{agent.usabilityScore}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ width: `${agent.usabilityScore}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'abtests' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">A/B Test Results</h3>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Create New Test
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {abTestData.map((test) => (
                      <div key={test.testId} className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-lg">{test.testName}</h4>
                            <p className="text-sm text-gray-500">{test.variant}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-lg text-sm font-medium ${getStatusColor(test.status)}`}>
                            {test.status}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">{test.conversionRate}%</div>
                            <div className="text-sm text-gray-600">Conversion Rate</div>
                          </div>
                          
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">{test.confidence}%</div>
                            <div className="text-sm text-gray-600">Confidence Level</div>
                          </div>
                          
                          <div className="text-center">
                            <div className={`text-2xl font-bold ${
                              test.confidence >= 95 ? 'text-green-600' : 
                              test.confidence >= 80 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {test.confidence >= 95 ? 'Significant' : 
                               test.confidence >= 80 ? 'Promising' : 'Inconclusive'}
                            </div>
                            <div className="text-sm text-gray-600">Result</div>
                          </div>
                        </div>
                        
                        {test.status === 'running' && (
                          <div className="mt-4 flex gap-2">
                            <button className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 transition-colors">
                              Pause Test
                            </button>
                            <button className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors">
                              End Test
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <h4 className="font-semibold text-blue-900 mb-3">A/B Testing Best Practices</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                      <div>
                        <h5 className="font-medium mb-2">Test Design:</h5>
                        <ul className="space-y-1 text-xs">
                          <li>• Test one variable at a time</li>
                          <li>• Ensure statistical significance</li>
                          <li>• Run tests for adequate duration</li>
                          <li>• Consider seasonal effects</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium mb-2">Metrics to Track:</h5>
                        <ul className="space-y-1 text-xs">
                          <li>• Conversion rates</li>
                          <li>• User engagement</li>
                          <li>• Time on page</li>
                          <li>• Click-through rates</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}